/**
 * The routes of the usermanagement-application.
 * In this file you define which page, layout and authorized roles belong to a path.
 * Some paths are also nested, these also have breadcrumbs for navigation.
 * Id fields in breadcrumbs need to be dynamicly added in the layout.
 * The router also does a token check before each route and redirects to a home page.
 */

import Vue from "vue";
import Router from "vue-router";
import {
    keycloak as Keycloak
} from "./main.js";
import keycloakConstants from "./constants/keycloakConstants.js";


const ORGANIZATIONS = "organisaties";
const EXCEPTIONS = "uitzonderingenlijst";
const SSH_KEYS = "userSSHKeys";
const USER_INFO = "gebruikersgegevens";
const DOCUMENTATION = "documentatie";
const ACCOUNT_CONSOLE = "inlogbeheer";
const NODES_INFO = "nodes";

const ACCOUNT_CONSOLE_URL = `${config.VUE_APP_KEYCLOAK_URL}/realms/${config.VUE_APP_KEYCLOAK_REALM}/account/#/security/signingin`;


Vue.use(Router);

const router = new Router({
    mode: "history",
    base: "/usermanagement/",
    routes: [
        {
            path: "/applicaties",
            name: "applicaties",
            meta: {
                layout: "oneColumn",
                authorized: [keycloakConstants.HDN_ADMIN, keycloakConstants.HDN_INZAGE]
            },
            component: () => import("@/pages/application.vue")
        },
        {
            path: "/applicaties/:applicationId",
            meta: {
                layout: "twoColumns",
                authorized: [keycloakConstants.HDN_ADMIN, keycloakConstants.HDN_INZAGE]
            },
            component: {
                render(c) {
                    return c("router-view");
                },
            },
            children: [
                {
                    path: "",
                    name: "home-applications",
                    redirect: {
                        name: "applicatiegegevens"
                    }
                },
                {
                    path: "gegevens",
                    name: "applicatiegegevens",
                    meta: {
                        layout: "twoColumns",
                        authorized: [keycloakConstants.HDN_ADMIN, keycloakConstants.HDN_INZAGE],
                        breadcrumb: [
                            { value: 'application.name'},
                            { name: 'Gegevens'}
                        ]
                    },
                    component: () => import("@/pages/applicatieGegevens.vue")
                },
                {
                    path: "functionaliteitautorisaties",
                    name: "functionaliteitautorisaties",
                    meta: {
                        layout: "twoColumns",
                        authorized: [keycloakConstants.HDN_ADMIN, keycloakConstants.HDN_INZAGE],
                        breadcrumb: [
                            { value: 'application.name'},
                            { name: 'Functionaliteitautorisaties'}
                        ]
                    },
                    component: () => import("@/pages/functionaliteitAutorisatie.vue")
                },
                {
                    path: "authenticatiemethoden",
                    name: "authenticatiemethoden",
                    meta: {
                        layout: "twoColumns",
                        authorized: [keycloakConstants.HDN_ADMIN, keycloakConstants.HDN_INZAGE],
                        breadcrumb: [
                            { value: 'application.name'},
                            { name: 'authenticatiemethoden'}
                        ]
                    },
                    component: () => import("@/pages/applicatieAuthenticatieMethoden.vue")
                },
            ]
        },
        {
            path: "/organisaties",
            name: "organisaties",
            meta: {
                layout: "oneColumn",
                authorized: [
                    keycloakConstants.HDN_ADMIN,
                    keycloakConstants.HDN_INZAGE,
                    keycloakConstants.ORG_ADMIN,
                    ...keycloakConstants.ALL_ORG_FUNCTIONALITIES
                ]
            },
            component: () => import("@/pages/leden.vue")
        },
        {
            path: "/gebruikersinstellingen",
            meta: {
                layout: "noRole",
                authorized: []
            },
            component: {
                render(c) {
                    return c("router-view");
                },
            },
            children: [
                {
                    path: "",
                    name: 'home',
                    redirect: {
                        name: USER_INFO
                    }
                },
                {
                    path: 'gebruikersgegevens',
                    name: USER_INFO,
                    meta: {
                        layout: "noRole",
                        authorized: [],
                        breadcrumb: [
                            { name: 'Mijn gegevens'}
                        ]
                    },
                    component: () => import("@/pages/mijnGegevens.vue")
                },
                {
                    path: ACCOUNT_CONSOLE,
                    name: ACCOUNT_CONSOLE,
                    redirect(to) {
                        window.location.replace(ACCOUNT_CONSOLE_URL);
                    }
                },
                {
                    path: 'sshKeys',
                    name: SSH_KEYS,
                    meta: {
                        layout: "noRole",
                        authorized: [],
                        breadcrumb: [
                            { name: 'SSH Keys'}
                        ]
                    },
                    component: () => import("@/pages/sshKeys.vue")
                },
                {
                    path: 'documentatie',
                    name: DOCUMENTATION,
                    meta: {
                        layout: "noRole",
                        authorized: [keycloakConstants.DOCUMENTATION_ACCESS],
                        breadcrumb: [
                            { name: 'Documentatie'}
                        ]
                    },
                    component: () => import("@/pages/documentatie.vue")
                }
            ]
        },
        {
            path: "/organisaties/:staticId",
            meta: {
                layout: "twoColumns",
                authorized: [
                    keycloakConstants.ORG_ADMIN,
                    keycloakConstants.HDN_ADMIN,
                    keycloakConstants.HDN_INZAGE,
                    keycloakConstants.CONTACTPERSONEN_ADMIN,
                    keycloakConstants.AUTORISATIE_ADMIN,
                    keycloakConstants.MACHTIGINGEN_ADMIN
                ]
            },
            component: {
                render(c) {
                    return c("router-view");
                },
            },
            children: [
                {
                    path: "",
                    name: "home",
                    redirect: {
                        name: "lidgegevens"
                    }
                },
                {
                    path: "gegevens",
                    name: "lidgegevens",
                    meta: {
                        layout: "twoColumns",
                        authorized: [
                            keycloakConstants.HDN_ADMIN,
                            keycloakConstants.HDN_INZAGE,
                            keycloakConstants.ORG_ADMIN,
                            ...keycloakConstants.ALL_ORG_FUNCTIONALITIES
                        ],
                        breadcrumb: [
                            { name: 'StaticOrganisatieID', value: 'lid.name'},
                            { name: 'Gegevens'}
                        ]
                    },
                    component: () => import("@/pages/lidGegevens.vue")
                },
                {
                    path: "contactpersonen",
                    name: "lidcontactpersonen",
                    meta: {
                        layout: "twoColumns",
                        authorized: [
                            keycloakConstants.HDN_ADMIN,
                            keycloakConstants.HDN_INZAGE,
                            keycloakConstants.ORG_ADMIN,
                            keycloakConstants.CONTACTPERSONEN_ADMIN
                        ],
                        breadcrumb: [
                            { name: 'StaticOrganisatieID', value: 'lid.name'},
                            { name: 'Contactpersonen'}
                        ]
                    },
                    component: () => import("@/pages/lidContactpersonen.vue")
                },
                {
                    path: "functionaliteitautorisaties",
                    name: "lidfunctionaliteitautorisaties",
                    meta: {
                        layout: "twoColumns",
                        authorized: [
                            keycloakConstants.HDN_ADMIN,
                            keycloakConstants.HDN_INZAGE,
                            keycloakConstants.ORG_ADMIN,
                            keycloakConstants.AUTORISATIE_ADMIN
                        ],
                        breadcrumb: [
                            { name: 'StaticOrganisatieID', link: 'home', value: 'lid.name'},
                            { name: 'Functionaliteitautorisaties'}
                        ]
                    },
                    component: () => import("@/pages/lidFunctionaliteitautorisaties.vue")
                },
                {
                    path: "contactopties",
                    name: "lidcontactopties",
                    meta: {
                        layout: "twoColumns",
                        authorized: [
                            keycloakConstants.HDN_ADMIN,
                            keycloakConstants.HDN_INZAGE,
                            keycloakConstants.ORG_ADMIN,
                            keycloakConstants.CONTACTPERSONEN_ADMIN
                        ],
                        breadcrumb: [
                            { name: 'StaticOrganisatieID', link: 'home', value: 'lid.name'},
                            { name: 'Contactopties'}
                        ]
                    },
                    component: () => import("@/pages/lidContactOpties.vue")
                },
                {
                    path: "gebruikersgroepen",
                    name: "lidgebruikersgroepen",
                    meta: {
                        layout: "twoColumns",
                        authorized: [
                            keycloakConstants.HDN_ADMIN,
                            keycloakConstants.HDN_INZAGE,
                            keycloakConstants.ORG_ADMIN,
                            keycloakConstants.AUTORISATIE_ADMIN
                        ],
                        breadcrumb: [
                            { name: 'StaticOrganisatieID', link: 'home', value: 'lid.name'},
                            { name: 'Gebruikersgroepen'}
                        ]
                    },
                    component: () => import("@/pages/lidGebruikersgroepen.vue")
                },
                {
                    path: "machtigingen",
                    name: "lidmachtigingen",
                    meta: {
                        layout: "twoColumns",
                        authorized: [
                            keycloakConstants.HDN_ADMIN,
                            keycloakConstants.HDN_INZAGE,
                            keycloakConstants.ORG_ADMIN,
                            keycloakConstants.MACHTIGINGEN_ADMIN
                        ],
                        breadcrumb: [
                            { name: 'StaticOrganisatieID', link: 'home', value: 'lid.name'},
                            { name: 'Machtigingen'}
                        ]
                    },
                    component: () => import("@/pages/lidMachtigingen.vue")
                },
                {
                    path: "facturatie-instellingen",
                    name: "facturatie-instellingen",
                    meta: {
                        layout: "twoColumns",
                        authorized: [keycloakConstants.FACTURATIE_GEGEVENS_INZAGE, keycloakConstants.FACTURATIE_GEGEVENS_BEHEER],
                        breadcrumb: [
                            { name: 'StaticOrganisatieID', link: 'home', value: 'lid.name'},
                            { name: 'Facturatie-instellingen'}
                        ]
                    },
                    component: () => import("@/pages/lidFacturatieGegevens.vue")
                },
                {
                    path: "gebruikersgroepen/:staticGebruikersgroepId",
                    meta: {
                        layout: "twoColumns",
                        authorized: [
                            keycloakConstants.HDN_ADMIN,
                            keycloakConstants.HDN_INZAGE,
                            keycloakConstants.ORG_ADMIN,
                            keycloakConstants.AUTORISATIE_ADMIN
                        ],
                    },
                    component: {
                        render(c) {
                            return c("router-view");
                        },
                    },
                    children: [
                        {
                            path: "gebruikers",
                            name: "lidgebruikersgroep",
                            meta: {
                                layout: "twoColumns",
                                authorized: [
                                    keycloakConstants.HDN_ADMIN,
                                    keycloakConstants.HDN_INZAGE,
                                    keycloakConstants.ORG_ADMIN,
                                    keycloakConstants.AUTORISATIE_ADMIN
                                ],
                                breadcrumb: [
                                    { name: 'StaticOrganisatieID', link: 'home', value: 'lid.name'},
                                    { name: 'Gebruikersgroepen', link: 'lidgebruikersgroepen'},
                                    { name: 'StaticGebruikersgroepID', value: 'gebruikersgroep.VersionUserGroup[0].name' },
                                    { name: 'Gebruikers'}
                                ]
                            },
                            component: () => import("@/pages/lidGebruikersgroep.vue")
                        },
                        {
                            path: "functionaliteiten",
                            name: "lidgebruikersgroepfunctionaliteiten",
                            meta: {
                                layout: "twoColumns",
                                authorized: [
                                    keycloakConstants.HDN_ADMIN,
                                    keycloakConstants.HDN_INZAGE,
                                    keycloakConstants.ORG_ADMIN,
                                    keycloakConstants.AUTORISATIE_ADMIN
                                ],
                                breadcrumb: [
                                    { name: 'StaticOrganisatieID', link: 'home', value: 'lid.name'},
                                    { name: 'Gebruikersgroepen', link: 'lidgebruikersgroepfunctionaliteiten'},
                                    { name: 'StaticGebruikersgroepID', value: 'gebruikersgroep.VersionUserGroup[0].name' },
                                    { name: 'Functionaliteitautorisaties'}
                                ]
                            },
                            component: () => import("@/pages/lidGebruikersgroepFunctionaliteiten")
                        }
                    ]
                },
            ]
        },
        {
            path: "/gebruikersgroepen",
            name: "gebruikersgroepen",
            meta: {
                layout: "oneColumn",
                authorized: [keycloakConstants.HDN_ADMIN, keycloakConstants.HDN_INZAGE]
            },
            component: () => import("@/pages/gebruikersgroepen.vue")
        },
        {
            path: "/gebruikers",
            name: "gebruikers",
            meta: {
                layout: "oneColumn",
                authorized: [keycloakConstants.HDN_ADMIN, keycloakConstants.HDN_INZAGE]
            },
            component: () => import("@/pages/gebruikers.vue")
        },
        {
            path: "/contactpersonen",
            name: "contactpersonen",
            meta: {
                layout: "oneColumn",
                authorized: [keycloakConstants.HDN_ADMIN, keycloakConstants.HDN_INZAGE]
            },
            component: () => import("@/pages/contactpersonen.vue")
        },
        {
            path: "/uitzonderingenlijst",
            name: "uitzonderingenlijst",
            meta: {
                layout: "oneColumn",
                authorized: [keycloakConstants.HDN_ADMIN, keycloakConstants.HDN_INZAGE, keycloakConstants.UITZONDERINGENLIJST_ADMIN]
            },
            component: () => import("@/pages/uitzonderingenlijst.vue")
        },
        {
            path: "/error",
            name: "error",
            meta:{
                layout: "oneColumn",
                authorized: [
                    keycloakConstants.HDN_ADMIN,
                    keycloakConstants.HDN_INZAGE,
                    keycloakConstants.ORG_ADMIN,
                    keycloakConstants.ALL_ORG_FUNCTIONALITIES,
                    keycloakConstants.UITZONDERINGENLIJST_ADMIN
                ]
            },
            component: () => import("@/pages/error.vue")
        }
    ]
});

router.beforeEach(async (to, from, next) => {
    try {
        let userRoles = Keycloak?.tokenParsed?.resource_access?.[keycloakConstants.USERMANAGEMENT_REALM_NAME]?.roles;

        if (to.name === 'error' || to.name === SSH_KEYS || to.name === USER_INFO || to.name === NODES_INFO) {
            next();
            return;
        }

        if (to.name === DOCUMENTATION && userRoles?.includes(keycloakConstants.DOCUMENTATION_ACCESS)) {
            next();
            return;
        }

        if (to.name === EXCEPTIONS && userRoles?.includes(keycloakConstants.UITZONDERINGENLIJST_ADMIN)) {
            next();
            return;
        }

        /**
         * If you have no roles, go to ssh keys
         * If you have the role DOCUMENTATION_ACCESS, UITZONDERINGENLIJST_ADMIN and no other roles
         * also go to ssh keys. 
         * This is needed because DOCUMENTATION_ACCESS and UITZONDERINGENLIJST_ADMIN only users are not allowed to
         * GET /members
         */
        if (!userRoles || 
            userRoles?.length == 0 || 
            (userRoles?.includes(keycloakConstants.DOCUMENTATION_ACCESS) && !userRoles[1]) ||
            (userRoles?.includes(keycloakConstants.UITZONDERINGENLIJST_ADMIN) && !userRoles[1]) ||
            (userRoles?.includes(keycloakConstants.DOCUMENTATION_ACCESS) && userRoles?.includes(keycloakConstants.UITZONDERINGENLIJST_ADMIN) && !userRoles[2])
        ) {
            next({name: USER_INFO});
            return;
        }

        if( //Only with these two roles can you access facturatie instellingen, not even HDN-admin
            userRoles?.includes(keycloakConstants.FACTURATIE_GEGEVENS_BEHEER) ||
            userRoles?.includes(keycloakConstants.FACTURATIE_GEGEVENS_INZAGE)
        ) {
            if (to.name === 'facturatie-instellingen') {
                next();
                return;
            }
        }

        if(userRoles?.includes(keycloakConstants.HDN_ADMIN)) {
            if(to?.meta?.authorized?.includes?.(keycloakConstants.HDN_ADMIN)) {
                next();
                return;
            }
            //keycloakConstants.HDN_ADMIN gets redirected to ORGANIZATIONS page by default.
            next({path: ORGANIZATIONS});
            return;
        }
        else if(userRoles?.includes(keycloakConstants.HDN_INZAGE)) {
            if(to?.meta?.authorized?.includes?.(keycloakConstants.HDN_INZAGE)) {
                next();
                return;
            }
            //keycloakConstants.HDN_INZAGE gets redirected to ORGANIZATIONS page by default.
            next({path: ORGANIZATIONS});
            return;
        }
        else if(
            userRoles?.includes(keycloakConstants.ORG_ADMIN) ||
            userRoles?.includes(keycloakConstants.CONTACTPERSONEN_ADMIN) ||
            userRoles?.includes(keycloakConstants.AUTORISATIE_ADMIN) ||
            userRoles?.includes(keycloakConstants.MACHTIGINGEN_ADMIN) ||
            userRoles?.includes(keycloakConstants.FACTURATIE_GEGEVENS_BEHEER) ||
            userRoles?.includes(keycloakConstants.FACTURATIE_GEGEVENS_INZAGE)
        ) {
            await setWithExpiry('empoweredRoles', 300000);
            await handleRoleRouting(to, next);
            return;
        }
        else{
            localStorage.clear();
            Keycloak.logout();
            return;
        }

    } catch(err) {
        next('error');
    }
})


async function handleRoleRouting(to, next) {
  
    /**
     * The path for the org admin needs to start with his include url and must
     * be authorized for ORG_ADMINs.
     */
    let memberId = await getMemberIdFromToken(Keycloak.tokenParsed);
    let homeUrl = getHomeUrlForOrgUser(memberId);
    

    if (!to.name) {
        next(homeUrl);
        return; 
    }

    //These are the pages that are always allowed to be visited when you have a usermanagement role
    if(to.name === 'organisaties'  || to.path === homeUrl) {
        next();
        return;
    }

    let getMemberIdFromURL = to?.path?.split('/')[2];
    let routeMemberId = parseInt(getMemberIdFromURL);
    let empoweredRolesForMember = getEmpoweredRolesForMember(routeMemberId);
    let userRoles = Keycloak?.tokenParsed?.resource_access?.[keycloakConstants.USERMANAGEMENT_REALM_NAME]?.roles;

    if (userCanAccesOwnMemberRoute(to, userRoles, memberId ,getMemberIdFromURL) || userIsEmpoweredForRoute(to, empoweredRolesForMember, userRoles)){
        next();
        return;
    }

    //otherwise we go to our default member page
    next(homeUrl);
    return; 
}



/**
 * userIsEmpoweredForRoute: Checks whether a user is empowered for
 * a particular route. It does so by performing the following checks:
 * 1) Does the member the user is part of have permissions to view the
 * page the user wants to access. I.e. is the member empowered to view
 * the page.
 * 2) Does the user have the roles necessary to view the particular
 * page it wants to view.
 * If both checks are true, the user is authorized to view the page.
 * @param <string> to The URL the user wants to go to.
 * @param <array> empoweredRolesForMember The roles the users member has for the
 * targeted member the user wants to view a page of.
 * @param <array> userRoles The roles from the users token.
 * @returns <boolean> True in case the user is authorized, false in case
 * the user is not authorized.
 */
function userIsEmpoweredForRoute(to, empoweredRolesForMember, userRoles){
    let isAuthorizedByMember = false;
    let isAuthorizedByToken = false;
    for (const role of empoweredRolesForMember) {
        if (to?.meta?.authorized?.includes?.(role)) {
            isAuthorizedByMember = true;
            break;
        }
    }

    for (const role of userRoles) {
        if (to?.meta?.authorized?.includes?.(role)) {
            isAuthorizedByToken = true;
            break;
        }
    }

    return isAuthorizedByMember && isAuthorizedByToken;
}


function userCanAccesOwnMemberRoute(to, userRoles, ownMemberId, routeMemberId){
    let isOwnMember = parseInt(ownMemberId, 10) === parseInt(routeMemberId, 10);
    let hasRouteRole = to?.meta?.authorized?.some(element => userRoles.includes(element));

    return isOwnMember && hasRouteRole;
}

/**
 *Gets all empoweredRoles based on memberId from token
*/
async function getEmpoweredFunctionalities(token) {
    const { Machtigingen } = await import("@/api/data");
    const { Leden } = await import("@/api/data");

    try {
        let memberName = token.groups[0].split('/')[1];
        let members = await Leden.getMembers();
        let memberId = null;

        for (const member of members) {
            if (member.name == memberName) {
                memberId = member.id;
                break;
            }
        }

        let empoweredRoles = await Machtigingen.getMemberFunctionalitiesMembers(memberId);

        //make sure we store only the right MFM's for usermanagement
        empoweredRoles = empoweredRoles.filter(
            (mfm) => mfm?.memberId === memberId
                &&   mfm?.VersionMemberFunctionalityMember?.[0]?.isDisabled === false
                &&   mfm?.VersionMemberFunctionalityMember?.[0]?.status === 'accepted'
                &&   mfm?.StaticFunctionalityMember?.StaticFunctionality?.StaticApplication?.name === keycloakConstants.USERMANAGEMENT_REALM_NAME
                &&   keycloakConstants.EMPOWERABLE_ADMIN_ROLES.includes(mfm?.StaticFunctionalityMember?.StaticFunctionality?.name)
        );
        return empoweredRoles;

    } catch(err) {
        throw new Error(`Failed to get group from token. ${err.message}`);
    }
}


async function getMemberIdFromToken(token) {
    /**
     * Importing Leden here as it needs a keycloak token. If this file is
     * included at the top, keycloak.token in axiosInstance.js does not exist
     * yet.
     */
    const { Leden } = await import("@/api/data");

    try {
        /**
         * If the user is allowed to go to the intended page, the user should
         * be part of the member included in the URL. The member is a group in
         * Keycloak, which is included in the users token. By definition the user
         * is part of the group(s) in the token. Since in the usermanagement
         * application a user can only be part of a single member, only one path of
         * groups is returned in the token.
         *
         * Example of returned path: /member/group
         *
         * To make sure the user is redirected to the correct page, the following
         * must be checked:
         * 1) Retrieve the first (which must be only) group from the token.
         * 2) Split the group path on / and take index 1
         * 3) Retrieve the member from the database and take its id.
         */

        /**
         * Because we get members by name, only one member is returned. Member
         * name is a unique field.
         */
        let memberName = token.groups[0].split('/')[1];
        let members = await Leden.getMembersByQuery({name: memberName});

        let memberId = null;

        for (const member of members) {
            if (member.name == memberName) {
                memberId = member.id;
            }
        }

        return memberId;

    } catch(err) {
        throw new Error(`Failed to get group from token. ${err.message}`);
    }
}

function getHomeUrlForOrgUser(memberId) {
    if(!memberId) {
        throw new Error('Missing member ID in member');
    }

    return `/${ORGANIZATIONS}/${memberId}/gegevens`;
}

async function setWithExpiry(key, ttl) {
    /**
     * If not present or valid, sets a new cookie with empoweredRoles with a 5 min ttl
v     */
    let validateExpiryTime = getWithExpiry(key);
    if (validateExpiryTime === null ) {
        let empoweredRoles = await getEmpoweredFunctionalities(Keycloak.tokenParsed);

        const now = new Date()

        const item = {
            value: empoweredRoles,
            expiry: now.getTime() + ttl,
        }

        localStorage.setItem(key, JSON.stringify(item));
    }
}

function getWithExpiry(key) {
	let itemEmpoweredRoles = localStorage.getItem(key)

	// if the item doesn't exist, return null
	if (!itemEmpoweredRoles) {
		return null;
	}

	const item = JSON.parse(itemEmpoweredRoles)
	const now = new Date()

	// compare the expiry time of the item with the current time
	if (now.getTime() > item.expiry) {
		// If the item is expired, delete the item from storage
		// and return null
		localStorage.removeItem('empoweredRoles')
		return null
	}

	return item.value
}

function getEmpoweredRolesForMember(memberId) {
    let empoweredRoles = JSON.parse(localStorage.getItem('empoweredRoles'));
    let results = [];
    for (const empoweredRole of empoweredRoles.value) {
        if (empoweredRole.StaticFunctionalityMember.StaticFunctionality.StaticApplication.name === 'usermanagement' && empoweredRole.StaticFunctionalityMember.memberId === memberId) {
            results.push(empoweredRole.StaticFunctionalityMember.StaticFunctionality.name);
        }
    }

    return results;
}


export { router }
